import { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM, { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import defaultContent from "../../../content/downlineProfile";
import { useAppDispatch, useAppSelector } from "../../../store";
import { hideDownlineProfile, hideProfile } from "../../../reducers/profile";
import FocusTrap from "../../../common/components/FocusTrap/FocusTrap";
import { IconClose } from "../../../common/icons/IconClose/IconClose";
import "../PlatinumProfile/DownlineProfile.scss";
import { useLockBodyScrolling } from "../../../common/util/cssUtils";
import { IconChevronRight } from "../../../common/icons/IconChevronRight/IconChevronRight";
import useProfileHistory from "../hooks/useProfileHistory";
import { useFullscreenPortalTarget } from "../../../common/util/fullscreenPortalTarget";
import {
  trackBackProfile,
  trackCloseProfile,
  trackDownlineProfileView,
} from "../PlatinumProfile/downlineProfileTealium";
import { setSelectedDownlineProfile } from "../../../reducers/downlineProfile";
import CustomerProfile from "./CustomerProfileBody";
import CustomerProfileDataLayer from "./CustomerProfileDataLayer";
import LegacyCustomerProfile from "../../CustomerProfile/CustomerProfile";

const CustomerProfileModal = () => {
  // todo: remove these once new profile is the default.
  const legacyProfileContent = [
    "profile",
    "order",
    "orderType",
    "vcsReasons",
    "leafCustomerPV",
    "leafErrorMessage",
    "leafSortInfo",
    "orderHistoryNoDataMsg",
    "common",
  ];
  const { t } = useTranslation(["downlineProfile", ...legacyProfileContent]);
  const dispatch = useAppDispatch();
  const affAbo = useAppSelector((s) => s.profile.affAbo);
  const { profile } = useAppSelector((store) => store.boot.configuration);
  const isV2Default = profile?.showCustomerProfileV2 ?? false;
  const modalRef = useRef<HTMLDivElement>(null);

  const isShowProfile = useAppSelector((s) => s.profile.isShowProfile);
  const profileType = useAppSelector((s) => s.profile.profileType);

  const profileParam = new URLSearchParams(window.location.search).get("customerProfile");
  const showCustomerV2Profile = profileParam === "v2" || (profileParam !== "v1" && isV2Default);
  const isCustomerProfileOpen = isShowProfile && profileType === "customer";
  const isVisible = isCustomerProfileOpen && showCustomerV2Profile;
  const { showBackButton, goBack, pushToHistory } = useProfileHistory(isVisible ? affAbo : "");
  const currentAffAbo = useAppSelector((s) => s.downlineProfile.affAbo);

  useEffect(() => {
    if (isVisible) {
      trackDownlineProfileView();
    }
  }, [isVisible]);

  const [portalTarget, setPortalTarget] = useState<HTMLDivElement | null>(null);

  // Keep the modal inside the active fullscreen subtree, if any.
  const fullscreenPortalTarget = useFullscreenPortalTarget();

  useEffect(() => {
    const target = isVisible
      ? (modalRef.current?.querySelector<HTMLDivElement>(".downline-profile__banner__nav-buttons") ?? null)
      : null;
    setPortalTarget(target);
  }, [isVisible, fullscreenPortalTarget]);

  const preventTouchMoves = useCallback((e: Event) => {
    const target = e.target as Node;
    if (modalRef?.current && modalRef.current.contains(target)) {
      // if touch is inside modal return to allow movement
      return;
    }
    if ((target as Element)?.closest?.(".metrics-toolbar__kpi-selector-portal, .kpi-tracking-filter-modal")) {
      // if touch is inside the KPI selector's portal (rendered outside modalRef's DOM subtree) return to allow movement
      return;
    }
    e.preventDefault();
  }, []);
  useLockBodyScrolling(isVisible, modalRef, preventTouchMoves);

  const handleClose = () => {
    dispatch(hideDownlineProfile());
    dispatch(hideProfile());
    dispatch(setSelectedDownlineProfile(""));
    trackCloseProfile();
  };
  if (!isCustomerProfileOpen) return null;

  if (!showCustomerV2Profile) return <LegacyCustomerProfile />;

  return ReactDOM.createPortal(
    <FocusTrap handleOnClose={handleClose}>
      <div
        ref={modalRef}
        className="downline-profile-modal"
        data-testid="downline-profile-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t("title", defaultContent.title) as string}
      >
        {showBackButton &&
          portalTarget &&
          createPortal(
            <button
              type="button"
              className="downline-profile-modal__back"
              data-testid="downline-profile-modal-back"
              onClick={() => {
                goBack();
                trackBackProfile();
              }}
              aria-label={t("back", defaultContent.back)}
            >
              <IconChevronRight color="black" />
              <p>{t("back", defaultContent.back)}</p>
            </button>,
            portalTarget,
          )}
        {portalTarget &&
          createPortal(
            <button
              type="button"
              className="downline-profile-modal__close"
              data-testid="downline-profile-modal-close"
              onClick={handleClose}
              aria-label={t("close", defaultContent.close as string)}
            >
              <IconClose width="1.5rem" height="1.5rem" />
            </button>,
            portalTarget,
          )}
        <div className="downline-profile-modal__body">
          <CustomerProfileDataLayer affAbo={currentAffAbo}>
            <CustomerProfile affAbo={currentAffAbo} pushToHistory={pushToHistory} />
          </CustomerProfileDataLayer>
        </div>
      </div>
      <div className="downline-profile-modal__hidden" aria-hidden={true}></div>
    </FocusTrap>,
    fullscreenPortalTarget,
  );
};

export default CustomerProfileModal;
